import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../config/apiConfig';
import LoadingState from '../../features/dashboard/widgets/Shared/LoadingState';
import EmptyState from '../../features/dashboard/widgets/Shared/EmptyState';
import Pagination from '../../shared/components/common/Pagination';
import ExportButton from '../../shared/components/modals/ExportButton';
import ExportColumnConfigurationModal from '../../shared/components/modals/ExportColumnConfigurationModal';
import {
  createAuditExportColumnsState,
  getSelectedExportColumnHeaders,
} from '../../features/audit/constants/auditExportColumns';
import {
  AUDIT_ACTION_QUICK_FILTERS,
  AUDIT_ENTITY_QUICK_FILTER_ORDER,
  AUDIT_TIME_QUICK_FILTERS,
  buildAuditQuickFilters,
  filterShowsLocationBadge,
  formatActionTypesForPill,
  getQuickFilterKey,
  selectedActionsIncludeLocationScope,
} from '../../features/audit/constants/auditQuickFilters';
import { AuditChangeTable, formatAuditStackedDateTime, formatRoleLabel } from '../../features/audit/shared';
import LocationScopeBadge from '../../shared/components/common/LocationScopeBadge';
import MoreFiltersButton from '../../shared/components/common/MoreFiltersButton';
import SortableTh from '../../shared/components/common/SortableTh';
import { exportFilteredAuditTrail } from '../../utils/exportUtils';
import LocationFilterMultiSelect from '../../shared/components/common/LocationFilterMultiSelect';
import LocationDisabledBadge from '../../shared/components/common/LocationDisabledBadge';
import { findLocationById, isLocationActive } from '../../shared/utils/locationDisplay';
import { useServerTableSort } from '../../shared/utils/serverTableSort';
import { getListPageSize, saveUiPreferencePatch } from '../../shared/utils/uiPreferences';
import './AuditViewerPage.css';

function getAuditActionBadgeClass(actionType) {
  switch (actionType) {
    case 'CREATE':
      return 'audit-action-badge audit-action-badge--create';
    case 'UPDATE':
      return 'audit-action-badge audit-action-badge--update';
    case 'DELETE':
      return 'audit-action-badge audit-action-badge--delete';
    case 'ENABLE':
      return 'audit-action-badge audit-action-badge--enable';
    case 'DISABLE':
      return 'audit-action-badge audit-action-badge--disable';
    default:
      return 'audit-action-badge';
  }
}

function buildQuery(params) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    if (Array.isArray(v)) {
      if (!v.length) return;
      searchParams.set(k, v.join(','));
      return;
    }
    if (String(v).trim() === '') return;
    searchParams.set(k, String(v));
  });
  const q = searchParams.toString();
  return q ? `?${q}` : '';
}

function AuditViewerPage({ token, user, locationId }) {
  const isSuperAdmin = user?.role === 'superadmin';
  const tableColSpan = isSuperAdmin ? 8 : 7;

  const [entities, setEntities] = useState([]);
  const [locations, setLocations] = useState([]);

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingRows, setLoadingRows] = useState(true);
  const [error, setError] = useState('');

  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    entityTypes: true,
    location: true,
    actionCategory: true,
    changedBy: true,
    time: true,
  });

  const toggleDrawerSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // filters
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]); // multi-select
  const [selectedLocationIds, setSelectedLocationIds] = useState([]); // superadmin only
  const [selectedActionTypes, setSelectedActionTypes] = useState([]);
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [drawerCategories, setDrawerCategories] = useState([]);
  const [q, setQ] = useState('');
  const [changedByEmployeeId, setChangedByEmployeeId] = useState('');
  const [changedByName, setChangedByName] = useState('');

  // time
  const [days, setDays] = useState('90'); // default
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [timePresetTouched, setTimePresetTouched] = useState(false);

  const createDrawerStateFromCurrent = useCallback(
    () => ({
      selectedEntityTypes,
      selectedLocationIds,
      selectedActionTypes,
      category,
      q,
      changedByEmployeeId,
      changedByName,
      days,
      from,
      to,
    }),
    [
      selectedEntityTypes,
      selectedLocationIds,
      selectedActionTypes,
      category,
      q,
      changedByEmployeeId,
      changedByName,
      days,
      from,
      to,
    ]
  );

  const [drawerFilters, setDrawerFilters] = useState(() => ({}));

  // pagination — use shared list_page_size preference
  const [limit, setLimit] = useState(getListPageSize);
  const [offset, setOffset] = useState(0);
  const { sorting, handleSortChange } = useServerTableSort('changed_at', 'desc');

  const handleSortChangeWithReset = useCallback(
    (column) => {
      handleSortChange(column);
      setOffset(0);
    },
    [handleSortChange]
  );

  const [result, setResult] = useState({ total_count: 0, returned_count: 0, transactions: [] });
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil((result.total_count || 0) / limit));

  const [expanded, setExpanded] = useState(() => new Set());
  const [detailsById, setDetailsById] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(() => new Set());

  const [exportColumns, setExportColumns] = useState(() => createAuditExportColumnsState(isSuperAdmin));
  const [showExportColumnsConfig, setShowExportColumnsConfig] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const singleEntityTypeForCategory = useMemo(() => {
    if (selectedEntityTypes.length === 1) return selectedEntityTypes[0];
    return '';
  }, [selectedEntityTypes]);

  const entityLabelByKey = useMemo(() => {
    const map = {};
    entities.forEach((ent) => {
      map[ent.key] = ent.label;
    });
    return map;
  }, [entities]);

  const drawerSingleEntityTypeForCategory = useMemo(() => {
    const ets = drawerFilters.selectedEntityTypes || [];
    if (ets.length === 1) return ets[0];
    return '';
  }, [drawerFilters.selectedEntityTypes]);

  const fetchMeta = useCallback(async () => {
    setLoadingMeta(true);
    setError('');
    try {
      const entRes = await axios.get(API_ENDPOINTS.AUDIT_VIEWER.ENTITIES, { headers });
      setEntities(entRes.data?.data?.entities || []);

      if (isSuperAdmin) {
        const locRes = await axios.get(API_ENDPOINTS.LOCATIONS.LIST, { headers });
        setLocations(locRes.data || []);
      }
    } catch (e) {
      console.error(e);
      setError('Failed to load audit viewer metadata.');
    } finally {
      setLoadingMeta(false);
    }
  }, [headers, isSuperAdmin]);

  const fetchCategories = useCallback(async () => {
    if (!singleEntityTypeForCategory) {
      setCategories([]);
      return;
    }
    try {
      const res = await axios.get(API_ENDPOINTS.AUDIT_VIEWER.CATEGORIES(singleEntityTypeForCategory), { headers });
      setCategories(res.data?.data?.available_categories || []);
    } catch (e) {
      console.error(e);
      setCategories([]);
    }
  }, [headers, singleEntityTypeForCategory]);

  const fetchDrawerCategories = useCallback(async () => {
    if (!drawerSingleEntityTypeForCategory) {
      setDrawerCategories([]);
      return;
    }
    try {
      const res = await axios.get(API_ENDPOINTS.AUDIT_VIEWER.CATEGORIES(drawerSingleEntityTypeForCategory), { headers });
      setDrawerCategories(res.data?.data?.available_categories || []);
    } catch (e) {
      console.error(e);
      setDrawerCategories([]);
    }
  }, [headers, drawerSingleEntityTypeForCategory]);

  const fetchRows = useCallback(async () => {
    setLoadingRows(true);
    setError('');
    try {
      const useDateRange = Boolean(from || to);
      const query = buildQuery({
        limit,
        offset,
        entity_type: selectedEntityTypes,
        action_type: selectedActionTypes.length ? selectedActionTypes : undefined,
        category: category || undefined,
        q: q || undefined,
        changed_by_employee_id: changedByEmployeeId || undefined,
        changed_by_name: changedByName || undefined,
        location_id: isSuperAdmin && selectedLocationIds.length ? selectedLocationIds : undefined,
        days: useDateRange ? undefined : days,
        from: useDateRange ? from : undefined,
        to: useDateRange ? to : undefined,
        sort_by: sorting.sort_by,
        sort_direction: sorting.sort_direction,
      });

      const res = await axios.get(`${API_ENDPOINTS.AUDIT_VIEWER.TRANSACTIONS}${query}`, { headers });
      setResult(res.data?.data || { total_count: 0, returned_count: 0, transactions: [] });
      setExpanded(new Set());
      setDetailsById({});
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || 'Failed to load audit transactions.');
      setResult({ total_count: 0, returned_count: 0, transactions: [] });
      setExpanded(new Set());
      setDetailsById({});
    } finally {
      setLoadingRows(false);
    }
  }, [
    headers,
    isSuperAdmin,
    selectedLocationIds,
    selectedEntityTypes,
    selectedActionTypes,
    category,
    q,
    changedByEmployeeId,
    changedByName,
    limit,
    offset,
    days,
    from,
    to,
    sorting.sort_by,
    sorting.sort_direction,
  ]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    if (showAdvancedPanel) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAdvancedPanel]);

  useEffect(() => {
    fetchCategories();
    setCategory(''); // reset when entity type selection changes
  }, [fetchCategories]);

  useEffect(() => {
    fetchDrawerCategories();
    // If drawer category is set but entity selection is no longer single, clear it.
    if (!drawerSingleEntityTypeForCategory && drawerFilters.category) {
      setDrawerFilters((prev) => ({ ...prev, category: '' }));
    }
  }, [fetchDrawerCategories, drawerSingleEntityTypeForCategory]);

  useEffect(() => {
    if (!loadingMeta) fetchRows();
  }, [loadingMeta, fetchRows]);

  const buildExportFilters = useCallback(() => {
    const useDateRange = Boolean(from || to);
    return {
      entity_type: selectedEntityTypes.length ? selectedEntityTypes : undefined,
      action_type: selectedActionTypes.length ? selectedActionTypes : undefined,
      category: category || undefined,
      q: q || undefined,
      changed_by_employee_id: changedByEmployeeId || undefined,
      changed_by_name: changedByName || undefined,
      location_id: isSuperAdmin && selectedLocationIds.length ? selectedLocationIds : undefined,
      days: useDateRange ? undefined : days,
      from: useDateRange ? from : undefined,
      to: useDateRange ? to : undefined,
    };
  }, [
    selectedEntityTypes,
    selectedActionTypes,
    category,
    q,
    changedByEmployeeId,
    changedByName,
    isSuperAdmin,
    selectedLocationIds,
    days,
    from,
    to,
  ]);

  const handleExportExcel = async () => {
    const selected = getSelectedExportColumnHeaders(exportColumns);
    if (!selected.length) {
      toast.error('Select at least one column to export');
      return;
    }
    setIsExporting(true);
    try {
      const { exportedRowCount } = await exportFilteredAuditTrail(buildExportFilters(), token, selected);
      const count = exportedRowCount ?? total;
      toast.success(
        count
          ? `Exported ${count} change record${count !== 1 ? 's' : ''} to Excel`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting audit trail:', error);
      toast.error(error.message || 'Failed to export Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const onToggleExpand = async (txnId) => {
    const next = new Set(expanded);
    if (next.has(txnId)) {
      next.delete(txnId);
      setExpanded(next);
      return;
    }
    next.add(txnId);
    setExpanded(next);

    if (detailsById[txnId] || loadingDetails.has(txnId)) return;

    const ld = new Set(loadingDetails);
    ld.add(txnId);
    setLoadingDetails(ld);
    try {
      const res = await axios.get(API_ENDPOINTS.AUDIT_VIEWER.TRANSACTION(txnId), { headers });
      setDetailsById((prev) => ({ ...prev, [txnId]: res.data?.data || null }));
    } catch (e) {
      console.error(e);
      setDetailsById((prev) => ({ ...prev, [txnId]: null }));
    } finally {
      const ld2 = new Set(ld);
      ld2.delete(txnId);
      setLoadingDetails(ld2);
    }
  };

  const onExpandAll = useCallback(async () => {
    const ids = (result.transactions || []).map((t) => t.id);
    if (!ids.length) return;

    setExpanded(new Set(ids));

    const missing = ids.filter((id) => !detailsById[id] && !loadingDetails.has(id));
    if (!missing.length) return;

    setLoadingDetails((prev) => {
      const next = new Set(prev);
      missing.forEach((id) => next.add(id));
      return next;
    });

    await Promise.all(
      missing.map(async (txnId) => {
        try {
          const res = await axios.get(API_ENDPOINTS.AUDIT_VIEWER.TRANSACTION(txnId), { headers });
          setDetailsById((prev) => ({ ...prev, [txnId]: res.data?.data || null }));
        } catch (e) {
          console.error(e);
          setDetailsById((prev) => ({ ...prev, [txnId]: null }));
        } finally {
          setLoadingDetails((prev) => {
            const next = new Set(prev);
            next.delete(txnId);
            return next;
          });
        }
      })
    );
  }, [result.transactions, detailsById, loadingDetails, headers]);

  const onCollapseAll = useCallback(() => {
    setExpanded(new Set());
  }, []);

  const total = result.total_count || 0;
  const rows = result.transactions || [];
  const allExpanded = rows.length > 0 && rows.every((t) => expanded.has(t.id));
  const anyExpanded = rows.some((t) => expanded.has(t.id));

  const hasActiveFilters = useMemo(() => {
    const useDateRange = Boolean(from || to);
    const isDefaultTime = !timePresetTouched && !useDateRange && String(days) === '90';
    return Boolean(
      (selectedEntityTypes && selectedEntityTypes.length) ||
        (isSuperAdmin && selectedLocationIds.length) ||
        selectedActionTypes.length ||
        category ||
        // Free-text search (`q`) is not an Active Filter — cleared via search box only
        changedByEmployeeId ||
        changedByName ||
        useDateRange ||
        (!isDefaultTime && Boolean(days))
    );
  }, [
    selectedEntityTypes,
    isSuperAdmin,
    selectedLocationIds,
    selectedActionTypes,
    category,
    changedByEmployeeId,
    changedByName,
    from,
    to,
    days,
    timePresetTouched,
  ]);

  const clearAllFilters = useCallback(() => {
    setSelectedEntityTypes([]);
    setSelectedLocationIds([]);
    setSelectedActionTypes([]);
    setCategory('');
    // Preserve free-text search (`q`)
    setChangedByEmployeeId('');
    setChangedByName('');
    setFrom('');
    setTo('');
    setDays('90');
    setTimePresetTouched(false);
    setOffset(0);
  }, []);

  const currentEmployeeId = user?.employee_id || '';

  const isTimePresetActive = useCallback(
    (presetDays) =>
      timePresetTouched && String(days) === String(presetDays) && !from && !to,
    [timePresetTouched, days, from, to]
  );

  const toggleTimePreset = useCallback(
    (presetDays) => {
      if (isTimePresetActive(presetDays)) {
        setDays('90');
        setTimePresetTouched(false);
      } else {
        setDays(String(presetDays));
        setFrom('');
        setTo('');
        setTimePresetTouched(true);
      }
      setOffset(0);
    },
    [isTimePresetActive]
  );

  const isEntityTypeActive = useCallback(
    (entityKey) => selectedEntityTypes.includes(entityKey),
    [selectedEntityTypes]
  );

  const toggleEntityType = useCallback((entityKey) => {
    setSelectedEntityTypes((prev) =>
      prev.includes(entityKey) ? prev.filter((t) => t !== entityKey) : [...prev, entityKey]
    );
    setOffset(0);
  }, []);

  const isActionTypeActive = useCallback(
    (type) => selectedActionTypes.includes(type),
    [selectedActionTypes]
  );

  const toggleActionType = useCallback((type) => {
    setSelectedActionTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
    setOffset(0);
  }, []);

  const isMyChangesActive = useCallback(
    () => Boolean(currentEmployeeId && changedByEmployeeId === currentEmployeeId),
    [currentEmployeeId, changedByEmployeeId]
  );

  const toggleMyChanges = useCallback(() => {
    if (!currentEmployeeId) return;
    setChangedByEmployeeId((prev) => (prev === currentEmployeeId ? '' : currentEmployeeId));
    setOffset(0);
  }, [currentEmployeeId]);

  const quickFilters = useMemo(
    () =>
      buildAuditQuickFilters({
        entities,
        isSuperAdmin,
        hasEmployeeId: Boolean(currentEmployeeId),
      }),
    [entities, isSuperAdmin, currentEmployeeId]
  );

  const quickActionTypes = useMemo(
    () => AUDIT_ACTION_QUICK_FILTERS
      .filter((filter) => !filter.superadminOnly || isSuperAdmin)
      .map((filter) => filter.actionType),
    [isSuperAdmin]
  );
  const quickTimeDays = useMemo(
    () => new Set(AUDIT_TIME_QUICK_FILTERS.map((filter) => Number(filter.days))),
    []
  );
  const hasAdditionalFilters = Boolean(
    (isSuperAdmin && selectedLocationIds.length)
    || category
    || changedByName
    || (changedByEmployeeId && changedByEmployeeId !== currentEmployeeId)
    || Boolean(from || to)
    || (timePresetTouched && !quickTimeDays.has(Number(days)))
    || (selectedEntityTypes || []).some((entityKey) => !AUDIT_ENTITY_QUICK_FILTER_ORDER.includes(entityKey))
    || (selectedActionTypes || []).some((actionType) => !quickActionTypes.includes(actionType))
  );

  const isQuickFilterActive = useCallback(
    (filter) => {
      switch (filter.type) {
        case 'all':
          return !hasActiveFilters;
        case 'actor':
          return isMyChangesActive();
        case 'entity':
          return isEntityTypeActive(filter.entityKey);
        case 'action':
          return isActionTypeActive(filter.actionType);
        case 'time':
          return isTimePresetActive(filter.days);
        default:
          return false;
      }
    },
    [hasActiveFilters, isMyChangesActive, isEntityTypeActive, isActionTypeActive, isTimePresetActive]
  );

  const handleQuickFilterClick = useCallback(
    (filter) => {
      switch (filter.type) {
        case 'all':
          clearAllFilters();
          break;
        case 'actor':
          toggleMyChanges();
          break;
        case 'entity':
          toggleEntityType(filter.entityKey);
          break;
        case 'action':
          toggleActionType(filter.actionType);
          break;
        case 'time':
          toggleTimePreset(filter.days);
          break;
        default:
          break;
      }
    },
    [clearAllFilters, toggleMyChanges, toggleEntityType, toggleActionType, toggleTimePreset]
  );

  return (
    <div className="audit-viewer-page">
      <div className="audit-viewer-card">
        <div className="audit-viewer-header">
          <div className="title-with-button">
            <h2 className="page-title">📜 Audit Trail</h2>
            <button
              type="button"
              onClick={fetchRows}
              className="btn-refresh"
              title="Refresh audit trail"
            >
              🔄
            </button>
          </div>
          <p className="audit-viewer-subtitle">
            Explore audited activity across entities{isSuperAdmin ? ' and locations' : ''}.
          </p>
        </div>

        {loadingMeta ? (
          <LoadingState message="Loading audit viewer…" />
        ) : (
          <>
            <div className="search-box-wrapper">
              <span className="search-icon-lens">🔍</span>
              <input
                type="text"
                placeholder="Search by entity identifier/name, actor, field name, or values..."
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setOffset(0);
                }}
                className="search-input-clean"
              />
              {q && (
                <button
                  className="clear-search-btn-inline"
                  onClick={() => setQ('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="quick-filters">
              <div className="quick-filters-title">
                <svg className="filters-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                Quick Filters
              </div>
              <div className="preset-buttons">
                {quickFilters.map((filter) => (
                  <button
                    key={getQuickFilterKey(filter)}
                    type="button"
                    className={`preset-btn ${isQuickFilterActive(filter) ? 'active' : ''}`}
                    onClick={() => handleQuickFilterClick(filter)}
                    disabled={Boolean(filter.disabled)}
                    title={filter.title}
                  >
                    {filter.label}
                    {filterShowsLocationBadge(filter) && <LocationScopeBadge />}
                  </button>
                ))}
                <MoreFiltersButton
                  isOpen={showAdvancedPanel}
                  hasAdditional={hasAdditionalFilters}
                  onClick={() => {
                    if (!showAdvancedPanel) setDrawerFilters(createDrawerStateFromCurrent());
                    setShowAdvancedPanel(!showAdvancedPanel);
                  }}
                />
              </div>
            </div>

            {hasActiveFilters && (
              <div className="active-filters">
                <div className="active-filters-header">
                  <span className="active-filters-label">Filters Active:</span>
                  <button className="clear-all-btn" onClick={clearAllFilters}>
                    Clear All
                  </button>
                </div>
                <div className="active-filters-pills">
                  {(() => {
                    const pills = [];
                    const pushWithAnd = (node) => {
                      if (pills.length > 0) {
                        pills.push(
                          <span key={`and-${pills.length}`} className="filter-and-connector"> AND </span>
                        );
                      }
                      pills.push(node);
                    };

                    if (selectedEntityTypes.length) {
                      pushWithAnd(
                        <div className="filter-pill" key="entityTypes-pill">
                          <span className="filter-pill-content">
                            Entity:{' '}
                            {selectedEntityTypes.map((key, index) => (
                              <React.Fragment key={key}>
                                {index > 0 ? ', ' : ''}
                                {entityLabelByKey[key] || key}
                                {key === 'location_complaint_tat_version' && <LocationScopeBadge />}
                              </React.Fragment>
                            ))}
                          </span>
                          <span
                            className="filter-pill-remove"
                            onClick={() => setSelectedEntityTypes([])}
                            role="button"
                            tabIndex={0}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (isSuperAdmin && selectedLocationIds.length) {
                      pushWithAnd(
                        <div className="filter-pill" key="location-pill">
                          <span className="filter-pill-content filter-pill-location-list">
                            Location:{' '}
                            {selectedLocationIds.map((id, index) => {
                              const loc = findLocationById(locations, id);
                              return (
                                <React.Fragment key={id}>
                                  {index > 0 ? ', ' : ''}
                                  {loc?.name || id}
                                  {loc && !isLocationActive(loc) && <LocationDisabledBadge />}
                                </React.Fragment>
                              );
                            })}
                          </span>
                          <span
                            className="filter-pill-remove"
                            onClick={() => setSelectedLocationIds([])}
                            role="button"
                            tabIndex={0}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (selectedActionTypes.length) {
                      pushWithAnd(
                        <div className="filter-pill" key="action-pill">
                          <span className="filter-pill-content">
                            Action: {formatActionTypesForPill(selectedActionTypes).join(', ')}
                            {selectedActionsIncludeLocationScope(selectedActionTypes) && <LocationScopeBadge />}
                          </span>
                          <span
                            className="filter-pill-remove"
                            onClick={() => setSelectedActionTypes([])}
                            role="button"
                            tabIndex={0}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (category) {
                      pushWithAnd(
                        <div className="filter-pill" key="category-pill">
                          Category: {category}
                          <span className="filter-pill-remove" onClick={() => setCategory('')} role="button" tabIndex={0}>
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (changedByEmployeeId) {
                      const myChangesLabel =
                        currentEmployeeId && changedByEmployeeId === currentEmployeeId
                          ? 'My changes'
                          : `By employee: ${changedByEmployeeId}`;
                      pushWithAnd(
                        <div className="filter-pill" key="employee-pill">
                          {myChangesLabel}
                          <span className="filter-pill-remove" onClick={() => setChangedByEmployeeId('')} role="button" tabIndex={0}>
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (changedByName) {
                      pushWithAnd(
                        <div className="filter-pill" key="name-pill">
                          By name: {changedByName}
                          <span className="filter-pill-remove" onClick={() => setChangedByName('')} role="button" tabIndex={0}>
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (from || to) {
                      pushWithAnd(
                        <div className="filter-pill" key="dateRange-pill">
                          Date range: {from || '…'} to {to || '…'}
                          <span
                            className="filter-pill-remove"
                            onClick={() => {
                              setFrom('');
                              setTo('');
                              setDays('90');
                              setTimePresetTouched(false);
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    } else if (timePresetTouched && days) {
                      pushWithAnd(
                        <div className="filter-pill" key="period-pill">
                          Period: Last {days}d
                          <span className="filter-pill-remove" onClick={() => { setDays('90'); setTimePresetTouched(false); }} role="button" tabIndex={0}>
                            ✕
                          </span>
                        </div>
                      );
                    }

                    return pills;
                  })()}
                </div>
              </div>
            )}

          {error && <div className="audit-viewer-error">{error}</div>}

          {loadingRows ? (
            <LoadingState message="Loading audit transactions…" />
          ) : rows.length ? (
            <>
              <div className="audit-results-toolbar">
                <div className="audit-results-info">
                  Showing {rows.length} of {total} transactions
                  {total > 0 && (
                    <span> — Export includes all changes matching these filters</span>
                  )}
                </div>
                <div className="audit-expand-controls">
                  <button
                    type="button"
                    className="audit-expand-control-btn"
                    onClick={onExpandAll}
                    disabled={allExpanded}
                    title="Expand all details on this page"
                  >
                    Expand all
                  </button>
                  <span className="audit-expand-control-sep" aria-hidden="true">
                    ·
                  </span>
                  <button
                    type="button"
                    className="audit-expand-control-btn"
                    onClick={onCollapseAll}
                    disabled={!anyExpanded}
                    title="Collapse all details"
                  >
                    Collapse all
                  </button>
                </div>
              </div>

              <div className="audit-export-section">
                <ExportButton
                  onExportPDF={() => {}}
                  onExportExcel={handleExportExcel}
                  title="Export filtered audit trail to Excel"
                  disabled={isExporting || total === 0}
                  excelOnly
                />
                <button
                  type="button"
                  className="audit-export-config-btn"
                  onClick={() => setShowExportColumnsConfig(true)}
                  title="Configure export columns"
                >
                  ⚙️
                </button>
              </div>

            <div className="audit-viewer-table-wrap">
              <table className="audit-viewer-table">
                <thead>
                  <tr>
                    <SortableTh column="changed_at" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                      When
                    </SortableTh>
                    <SortableTh column="changed_by_name" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                      Changed by
                    </SortableTh>
                    <SortableTh column="action_type" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                      Action
                    </SortableTh>
                    <SortableTh column="entity_type" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                      Entity type
                    </SortableTh>
                    <SortableTh
                      column="entity"
                      sorting={sorting}
                      onSortChange={handleSortChangeWithReset}
                      className="col-entity"
                      title="Shown as name (ID). Name is the display label at the time of the change; ID is the stable code."
                    >
                      <span className="audit-th-stacked">
                        <span className="audit-th-label">Entity</span>
                        <span className="audit-th-hint">Name (ID)</span>
                      </span>
                    </SortableTh>
                    {isSuperAdmin && (
                      <SortableTh column="location" sorting={sorting} onSortChange={handleSortChangeWithReset}>
                        Location
                      </SortableTh>
                    )}
                    <SortableTh
                      column="change_count"
                      sorting={sorting}
                      onSortChange={handleSortChangeWithReset}
                      className="col-delta"
                    >
                      Δ
                    </SortableTh>
                    <th className="col-expand" aria-label="Expand details" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => {
                    const isExpanded = expanded.has(t.id);
                    const detail = detailsById[t.id];
                    const when = formatAuditStackedDateTime(t.changed_at);
                    return (
                      <React.Fragment key={t.id}>
                        <tr
                          className={`audit-txn-row${isExpanded ? ' is-expanded' : ''}`}
                          onClick={() => onToggleExpand(t.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onToggleExpand(t.id);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? 'Hide details' : 'Show details'}
                          title={isExpanded ? 'Hide details' : 'Show details'}
                        >
                          <td className="stacked-info audit-when-cell">
                            {when.date ? (
                              <>
                                <div className="stack-item audit-when-date">{when.date}</div>
                                <div className="stack-item audit-when-time">{when.time}</div>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <div className="who">
                              <div className="who-name">{t.changed_by_name || '—'}</div>
                              <div className="who-role">{formatRoleLabel(t.changed_by_role)}</div>
                            </div>
                          </td>
                          <td>
                            <span className={getAuditActionBadgeClass(t.action_type)}>
                              {t.action_type}
                            </span>
                          </td>
                          <td>{t.entity_type_label || t.entity_type}</td>
                          <td
                            title={
                              t.entity_name && t.entity_identifier
                                ? `Name: ${t.entity_name}\nID: ${t.entity_identifier}`
                                : t.entity_name || t.entity_identifier || ''
                            }
                          >
                            {t.entity_display || t.entity_identifier || `#${t.entity_id}`}
                          </td>
                          {isSuperAdmin && (
                            <td title={t.location_name || ''}>
                              {t.location_code ?? '—'}
                            </td>
                          )}
                          <td className="col-delta">{t.change_count ?? 0}</td>
                          <td className="col-expand">
                            <span
                              className={`audit-expand-chevron${isExpanded ? ' is-expanded' : ''}`}
                              aria-hidden="true"
                            />
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="details-row">
                            <td colSpan={tableColSpan}>
                              {loadingDetails.has(t.id) ? (
                                <LoadingState message="Loading changes…" />
                              ) : detail ? (
                                <AuditChangeTable changes={detail.changes || []} />
                              ) : (
                                <EmptyState message="No details available." />
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {total > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(newPage) => setOffset((newPage - 1) * limit)}
                  perPage={limit}
                  onPerPageChange={(newPerPage) => {
                    saveUiPreferencePatch({ list_page_size: newPerPage });
                    setLimit(newPerPage);
                    setOffset(0);
                  }}
                  totalItems={total}
                  itemLabel="transactions"
                />
              )}
            </div>
            </>
          ) : (
            <EmptyState message="No audit transactions match your filters." />
          )}

          <ExportColumnConfigurationModal
            isOpen={showExportColumnsConfig}
            onClose={() => setShowExportColumnsConfig(false)}
            title="Configure Export Columns"
            description="A focused set of audit fields is pre-selected. Add or remove columns to control what is included in the Excel export."
            columns={exportColumns}
            onColumnsChange={setExportColumns}
          />

          {showAdvancedPanel &&
            ReactDOM.createPortal(
              <div className="sidebar-drawer-overlay" onClick={() => setShowAdvancedPanel(false)}>
                <div className="audit-filters-drawer" onClick={(e) => e.stopPropagation()}>
                  <div className="drawer-title">
                    <span>🔧 Advanced Filters</span>
                    <button
                      className="drawer-close-btn"
                      onClick={() => setShowAdvancedPanel(false)}
                      title="Close filters"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="drawer-filter-content">
                    <div className="filter-group">
                      <div
                        className="filter-group-title"
                        onClick={() => toggleDrawerSection('entityTypes')}
                      >
                        <span className={`group-arrow ${expandedSections.entityTypes ? 'open' : ''}`}>▼</span>
                        Entity types
                      </div>
                      {expandedSections.entityTypes && (
                      <div className="filter-group-content">
                        <div className="filter-item-wrapper full-width">
                          <select
                            className="filter-select"
                            multiple
                            value={drawerFilters.selectedEntityTypes || []}
                            onChange={(e) => {
                              const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                              setDrawerFilters((prev) => ({ ...prev, selectedEntityTypes: selected }));
                            }}
                            style={{ minHeight: '90px' }}
                          >
                            {entities.map((ent) => (
                              <option key={ent.key} value={ent.key}>
                                {ent.label}
                              </option>
                            ))}
                          </select>
                          <small className="filter-help-text">
                            Leave empty to include all entity types. Select exactly one to enable the Category filter below.
                          </small>
                        </div>
                      </div>
                      )}
                    </div>

                    {isSuperAdmin && (
                      <div className="filter-group">
                        <div
                          className="filter-group-title"
                          onClick={() => toggleDrawerSection('location')}
                        >
                          <span className={`group-arrow ${expandedSections.location ? 'open' : ''}`}>▼</span>
                          Location
                        </div>
                        {expandedSections.location && (
                        <div className="filter-group-content">
                          <div className="filter-item-wrapper full-width">
                            <LocationFilterMultiSelect
                              locations={locations}
                              value={drawerFilters.selectedLocationIds || []}
                              onChange={(selected) =>
                                setDrawerFilters((prev) => ({ ...prev, selectedLocationIds: selected }))
                              }
                            />
                            <small className="filter-help-text">
                              Leave empty to include all locations. Hold Cmd/Ctrl to select multiple.
                            </small>
                          </div>
                        </div>
                        )}
                      </div>
                    )}

                    <div className="filter-group">
                      <div
                        className="filter-group-title"
                        onClick={() => toggleDrawerSection('actionCategory')}
                      >
                        <span className={`group-arrow ${expandedSections.actionCategory ? 'open' : ''}`}>▼</span>
                        Action + Category
                      </div>
                      {expandedSections.actionCategory && (
                      <div className="filter-group-content">
                        <div className="filter-item-wrapper full-width">
                          <span className="filter-label-text">Action</span>
                          <select
                            className="filter-select"
                            multiple
                            value={drawerFilters.selectedActionTypes || []}
                            onChange={(e) => {
                              const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                              setDrawerFilters((prev) => ({ ...prev, selectedActionTypes: selected }));
                            }}
                            style={{ minHeight: '90px' }}
                          >
                            <option value="CREATE">CREATE</option>
                            <option value="UPDATE">UPDATE</option>
                            <option value="DELETE">DELETE</option>
                            <option value="ENABLE">ENABLE</option>
                            <option value="DISABLE">DISABLE</option>
                          </select>
                          <small className="filter-help-text">
                            Leave empty for all actions. Select multiple to match any (OR). Hold Cmd/Ctrl to select more than one.
                          </small>
                        </div>
                        <div className="filter-item-wrapper">
                          <span className="filter-label-text">Category</span>
                          <select
                            className="filter-select"
                            value={drawerFilters.category || ''}
                            onChange={(e) => setDrawerFilters((prev) => ({ ...prev, category: e.target.value }))}
                            disabled={!drawerFilters.selectedEntityTypes || drawerFilters.selectedEntityTypes.length !== 1}
                            title={
                              drawerFilters.selectedEntityTypes && drawerFilters.selectedEntityTypes.length === 1
                                ? ''
                                : 'Select exactly one entity type to enable category filter'
                            }
                          >
                            <option value="">All</option>
                            {drawerCategories.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                          <small className="filter-help-text">
                            {(drawerFilters.selectedEntityTypes || []).length === 0 &&
                              'Category is disabled until you select exactly one entity type above.'}
                            {(drawerFilters.selectedEntityTypes || []).length > 1 &&
                              'Category is disabled when multiple entity types are selected. Choose only one entity type.'}
                            {(drawerFilters.selectedEntityTypes || []).length === 1 &&
                              'Optional. Filter audit changes by field category for the selected entity type.'}
                          </small>
                        </div>
                      </div>
                      )}
                    </div>

                    <div className="filter-group">
                      <div
                        className="filter-group-title"
                        onClick={() => toggleDrawerSection('changedBy')}
                      >
                        <span className={`group-arrow ${expandedSections.changedBy ? 'open' : ''}`}>▼</span>
                        Changed by
                      </div>
                      {expandedSections.changedBy && (
                      <>
                      <small className="filter-help-text filter-group-intro">
                        Optional. Use one or both fields. If both are filled, a record must match employee ID and name.
                      </small>
                      <div className="filter-group-content">
                        <div className="filter-item-wrapper">
                          <span className="filter-label-text">Employee ID</span>
                          <input
                            value={drawerFilters.changedByEmployeeId || ''}
                            onChange={(e) => setDrawerFilters((prev) => ({ ...prev, changedByEmployeeId: e.target.value }))}
                            placeholder="E001"
                          />
                          <small className="filter-help-text">Exact match only — enter the full employee ID.</small>
                        </div>
                        <div className="filter-item-wrapper">
                          <span className="filter-label-text">Name</span>
                          <input
                            value={drawerFilters.changedByName || ''}
                            onChange={(e) => setDrawerFilters((prev) => ({ ...prev, changedByName: e.target.value }))}
                            placeholder="Jane"
                          />
                          <small className="filter-help-text">Partial match — finds names containing this text (case-insensitive).</small>
                        </div>
                      </div>
                      </>
                      )}
                    </div>

                    <div className="filter-group">
                      <div
                        className="filter-group-title"
                        onClick={() => toggleDrawerSection('time')}
                      >
                        <span className={`group-arrow ${expandedSections.time ? 'open' : ''}`}>▼</span>
                        Time
                      </div>
                      {expandedSections.time && (
                      <>
                      <small className="filter-help-text filter-group-intro">
                        Use either a preset period or a custom date range — not both at the same time.
                      </small>

                      <div className="audit-time-filter-block audit-time-preset-block">
                        <div className="audit-time-filter-block-label">Preset period</div>
                        <div className="audit-time-field">
                          <span className="filter-label-text">Period</span>
                          <select
                            className="filter-select"
                            value={drawerFilters.days || '90'}
                            onChange={(e) =>
                              setDrawerFilters((prev) => ({
                                ...prev,
                                days: e.target.value,
                                from: '',
                                to: '',
                              }))
                            }
                            disabled={Boolean(drawerFilters.from || drawerFilters.to)}
                          >
                            <option value="7">Last 7 days</option>
                            <option value="30">Last 30 days</option>
                            <option value="90">Last 90 days</option>
                            <option value="365">Last 365 days</option>
                          </select>
                          <small className="filter-help-text">
                            {drawerFilters.from || drawerFilters.to
                              ? 'Disabled while a custom date range is set. Clear From/To below to use a preset.'
                              : 'Relative to today. Selecting a preset clears any custom dates.'}
                          </small>
                        </div>
                      </div>

                      <div className="audit-time-filter-divider" aria-hidden="true">
                        <span>or</span>
                      </div>

                      <div className="audit-time-filter-block">
                        <div className="audit-time-filter-block-label">Custom date range</div>
                        <div className="filter-group-content audit-time-range-fields">
                          <div className="filter-item-wrapper">
                            <span className="filter-label-text">From</span>
                            <input
                              type="datetime-local"
                              className="filter-select"
                              value={drawerFilters.from || ''}
                              onChange={(e) => setDrawerFilters((prev) => ({ ...prev, from: e.target.value }))}
                            />
                          </div>
                          <div className="filter-item-wrapper">
                            <span className="filter-label-text">To</span>
                            <input
                              type="datetime-local"
                              className="filter-select"
                              value={drawerFilters.to || ''}
                              onChange={(e) => setDrawerFilters((prev) => ({ ...prev, to: e.target.value }))}
                            />
                          </div>
                        </div>
                        <small className="filter-help-text audit-time-range-help">
                          {drawerFilters.from || drawerFilters.to
                            ? 'Custom range is active — Period is ignored. Use From, To, or both to bound the range.'
                            : 'Enter From and/or To to filter by specific dates. This replaces the preset period above.'}
                        </small>
                      </div>
                      </>
                      )}
                    </div>
                  </div>

                  <div className="drawer-buttons">
                    <button className="cancel-btn" onClick={() => setShowAdvancedPanel(false)}>
                      Close
                    </button>
                    <button
                      className="reset-btn"
                      onClick={() =>
                        setDrawerFilters({
                          selectedEntityTypes: [],
                          selectedLocationIds: [],
                          selectedActionTypes: [],
                          category: '',
                          q,
                          changedByEmployeeId: '',
                          changedByName: '',
                          days: '90',
                          from: '',
                          to: '',
                        })
                      }
                    >
                      Reset
                    </button>
                    <button
                      className="apply-btn"
                      onClick={() => {
                        setSelectedEntityTypes(drawerFilters.selectedEntityTypes || []);
                        setSelectedLocationIds(drawerFilters.selectedLocationIds || []);
                        setSelectedActionTypes(drawerFilters.selectedActionTypes || []);
                        setCategory(drawerFilters.category || '');
                        setChangedByEmployeeId(drawerFilters.changedByEmployeeId || '');
                        setChangedByName(drawerFilters.changedByName || '');
                        setDays(drawerFilters.days || '90');
                        setFrom(drawerFilters.from || '');
                        setTo(drawerFilters.to || '');
                        setTimePresetTouched(Boolean(drawerFilters.from || drawerFilters.to || String(drawerFilters.days || '90') !== '90'));
                        setOffset(0);
                        setShowAdvancedPanel(false);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AuditViewerPage;

